import queryString from "query-string";
import { useCallback, useEffect, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { useHistory, useLocation } from "react-router-dom";

import { pushAdTrackingEvent } from "../../../utils/adTracking";
import { getFullName, validateEmail } from "../../../utils/helper";
import {
  AuthCard,
  AuthContainer,
  AuthNavLink,
  AuthSection,
  AuthText,
  BackButton,
  ButtonSection,
  CodeInputContainer,
  CodeInputField,
  CodeInputLabel,
  ErrorMessage,
  FormSection,
  HeaderSection,
  InputContainer,
  InputGroup,
  PrimaryButton,
  ServerErrorAlert,
  Subtitle,
  Title,
} from "../shared/authStyles";
import { BackIcon } from "../shared/icons/AuthIcons";
import { AnimatePresence } from "../shared/motionUtils";

const EmailVerification = ({
  verifyCode,
  resendVerificationCode,
  resetVerifyCode,
  verificationCode,
  isLoading,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [serverError, setServerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [query, setQuery] = useState(null);
  const firstCodeInputRef = useRef(null);

  const tagManagerArgs = {
    gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
  };

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
    let token = localStorage.getItem("access_token");

    if (token || !location.search) {
      history.push("/auth/login");
      return;
    }

    const queryParsed = queryString.parse(location.search);
    setQuery(queryParsed);

    validateEmail(queryParsed.email);

    const isValidEmail =
      queryParsed?.email?.length > 0 && validateEmail(queryParsed.email);
    if (!isValidEmail) {
      history.push("/auth/login");
      return;
    }
    setEmail(queryParsed.email);
    setName(queryParsed.name);

    if (location?.state?.from === "login") {
      resendVerificationCode({ email: queryParsed.email });
    }

    return () => {
      resetVerifyCode();
    };
  }, [
    history,
    location.search,
    location.state,
    resendVerificationCode,
    resetVerifyCode,
  ]);

  // Auto-focus first code input field on component mount
  useEffect(() => {
    if (firstCodeInputRef.current) {
      firstCodeInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (verificationCode && verificationCode.redirect) {
      if (verificationCode.token && verificationCode.userData) {
        localStorage.setItem("access_token", verificationCode.token);
        localStorage.setItem("user_id", verificationCode.userData.user_id);
        localStorage.setItem(
          "user_profile",
          verificationCode.userData.user_profile.id,
        );
        localStorage.setItem(
          "user_fullName",
          getFullName(verificationCode.userData.user_profile),
        );
        if (verificationCode.userData.user_profile.color) {
          localStorage.setItem(
            "user_profile_color",
            verificationCode.userData.user_profile.color,
          );
        }
        localStorage.setItem("email", verificationCode.userData.email);

        setTimeout(() => {
          // OnboardingWrapper will handle onboarding check automatically
          // Store attribution data if available
          if (query?.ad_platform && query?.ad_platform.length > 0) {
            const onboardingData = {
              ad_platform: query.ad_platform,
              name: query.last_name ? `${name} ${query.last_name}` : name,
              email: email,
            };
            localStorage.setItem(
              "onboarding_attribution",
              JSON.stringify(onboardingData),
            );
          }

          // Redirect to dashboard - modal will show there
          history.push("/user/dashboard");
        }, 400);
      } else {
        history.push("/auth/login");
      }
      setTimeout(() => {
        resetVerifyCode();
      }, 500);
    }
  }, [verificationCode, history, query, name, email, resetVerifyCode]);

  useEffect(() => {
    if (code && code.length === 4) {
      let payload = {
        email,
        code,
      };
      verifyCode(payload);
    }
  }, [code, email, verifyCode]);

  useEffect(() => {
    if (query?.ad_platform && query?.ad_platform.length > 0) {
      pushAdTrackingEvent({
        event: "signup_form_submitted",
        location,
        user_id: verificationCode?.userData?.user_id || null,
        full_name: query?.last_name
          ? `${query.name} ${query.last_name}`
          : query.name || null,
        email: query.email || null,
      });
    }
  }, [query]);

  const handleSubmitCode = useCallback(
    e => {
      e.preventDefault();
      let payload = {
        email,
        code,
      };
      verifyCode(payload);
    },
    [email, code, verifyCode],
  );

  const handleResendCode = useCallback(() => {
    let payload = {
      email,
    };
    resendVerificationCode(payload);
  }, [email, resendVerificationCode]);

  const handleBack = useCallback(() => {
    history.push("/auth/login");
  }, [history]);

  const handleCodeChange = useCallback(
    newCode => {
      setCode(newCode);
      // Clear any errors when user starts typing
      if (serverError) {
        setServerError(null);
      }
      if (fieldErrors.code) {
        setFieldErrors(prev => ({ ...prev, code: null }));
      }
    },
    [serverError, fieldErrors.code],
  );

  const handleCodeInputChange = useCallback(
    (index, value) => {
      // Only allow single digit
      if (value.length > 1) return;

      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode.join(""));

      // Auto-focus next field
      if (value && index < 3) {
        const nextInput = document.querySelector(
          `input[data-index="${index + 1}"]`,
        );
        if (nextInput) nextInput.focus();
      }
    },
    [code],
  );

  const handleCodeKeyDown = useCallback(
    (index, e) => {
      // Backspace navigation
      if (e.key === "Backspace" && !code[index] && index > 0) {
        const prevInput = document.querySelector(
          `input[data-index="${index - 1}"]`,
        );
        if (prevInput) prevInput.focus();
      }

      // Arrow key navigation
      if (e.key === "ArrowLeft" && index > 0) {
        const prevInput = document.querySelector(
          `input[data-index="${index - 1}"]`,
        );
        if (prevInput) prevInput.focus();
      }
      if (e.key === "ArrowRight" && index < 3) {
        const nextInput = document.querySelector(
          `input[data-index="${index + 1}"]`,
        );
        if (nextInput) nextInput.focus();
      }
    },
    [code],
  );

  const handleCodePaste = useCallback(e => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);

    // Only allow digits
    if (!/^\d+$/.test(pastedData)) return;

    setCode(pastedData);

    // Focus last filled input
    const focusIndex = Math.min(pastedData.length - 1, 3);
    const targetInput = document.querySelector(
      `input[data-index="${focusIndex}"]`,
    );
    if (targetInput) targetInput.focus();
  }, []);

  return (
    <AuthContainer>
      <AuthCard
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        style={{
          width: "488px",
          maxWidth: "488px",
          minWidth: "320px",
        }}
      >
        <HeaderSection marginBottom="0" alignItems="flex-start">
          <Title
            margin="0"
            fontSize="32px"
            lineHeight="48px"
            color="#000e2a"
            textAlign="left"
          >
            Verify your email
          </Title>
          <Subtitle
            fontSize="14px"
            lineHeight="21px"
            color="#353a46"
            align="left"
            margin="8px 0 0"
          >
            We sent a 4‑digit code to your email
          </Subtitle>
        </HeaderSection>

        <FormSection
          as="form"
          onSubmit={handleSubmitCode}
          marginTop="32px"
          width="400px"
        >
          <InputContainer style={{ width: "100%", maxWidth: "100%" }}>
            <InputGroup style={{ width: "100%", maxWidth: "100%" }}>
              <CodeInputLabel>Enter 4 digit code</CodeInputLabel>
              <CodeInputContainer>
                {[0, 1, 2, 3].map(index => (
                  <CodeInputField
                    key={index}
                    ref={index === 0 ? firstCodeInputRef : null}
                    data-index={index}
                    type="number"
                    maxLength={1}
                    value={code[index] || ""}
                    onChange={e => handleCodeInputChange(index, e.target.value)}
                    onKeyDown={e => handleCodeKeyDown(index, e)}
                    onPaste={e => handleCodePaste(e)}
                    autoComplete="off"
                  />
                ))}
              </CodeInputContainer>
              <AnimatePresence>
                {fieldErrors.code && (
                  <ErrorMessage
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {fieldErrors.code}
                  </ErrorMessage>
                )}
              </AnimatePresence>
            </InputGroup>
          </InputContainer>

          <AnimatePresence>
            {serverError && (
              <ServerErrorAlert
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {serverError}
              </ServerErrorAlert>
            )}
          </AnimatePresence>

          <ButtonSection direction="row" gap="12px" width="100%">
            <BackButton
              type="button"
              onClick={handleBack}
              width="100px"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <BackIcon size="20px" />
              Back
            </BackButton>
            <PrimaryButton
              type="submit"
              disabled={!code || (code && code.length < 4) || isLoading}
              flex="1"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? "Verifying..." : "Verify"}
            </PrimaryButton>
          </ButtonSection>

          <AuthSection>
            <AuthText color="#353a46">Don't received code yet?</AuthText>
            <AuthNavLink
              as="button"
              type="button"
              onClick={handleResendCode}
              color="#2266ef"
              style={{ background: "none", border: "none", padding: 0 }}
            >
              Resend code
            </AuthNavLink>
          </AuthSection>
        </FormSection>
      </AuthCard>
    </AuthContainer>
  );
};

export default EmailVerification;
